import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getStatusConfig } from '../config/statusConfig';
import { formatDate } from '../utils/dateUtils';

const iconForMimeType = (mimeType) => {
  if (!mimeType) return <InsertDriveFileIcon fontSize="large" color="disabled" />;
  if (mimeType.startsWith('image/')) return <ImageIcon fontSize="large" color="action" />;
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon fontSize="large" color="action" />;
  return <InsertDriveFileIcon fontSize="large" color="action" />;
};

const CodexThumbnailCard = ({ codex, onOpen, isLoadingPreview = false }) => {
  const status = getStatusConfig('CODEX', codex.status);
  const isDraft = codex.status === 'DRAFT';
  const showPlaceholder = !codex.previewUrl;
  const createdLabel = formatDate(codex.createdAt || codex.createdDate || codex.createdOn);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea onClick={onOpen} sx={{ flexGrow: 1, alignItems: 'stretch' }}>
        {codex.codexTypeCode && (
          <Box
            sx={{
              px: 2,
              py: 1,
              backgroundColor: 'action.hover',
              color: 'text.primary',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            <span>{codex.codexTypeCode}</span>
            {isDraft ? (
              <CircularProgress size={18} thickness={5} sx={{ ml: 1 }} />
            ) : (
              <Chip
                label={status.label}
                size="small"
                color={status.color}
                sx={{
                  ml: 1,
                  height: 22,
                  fontWeight: 600,
                  backgroundColor: status.color === 'default' ? 'rgba(0,0,0,0.12)' : undefined,
                  color: status.color === 'default' ? 'text.primary' : undefined,
                  '& .MuiChip-label': { px: 1.5 }
                }}
              />
            )}
          </Box>
        )}
        {!codex.codexTypeCode && (
          <Box sx={{ px: 2, pt: 2 }}>
            {isDraft ? (
              <CircularProgress size={18} thickness={5} />
            ) : (
              <Chip label={status.label} color={status.color} size="small" />
            )}
          </Box>
        )}
        <Box
          sx={{
            position: 'relative',
            pt: '100%',
            backgroundColor: 'grey.100',
            overflow: 'hidden',
            mt: codex.codexTypeCode ? 1 : 0,
          }}
        >
          {codex.previewUrl && (
            <CardMedia
              component="img"
              image={codex.previewUrl}
              alt={codex.previewFileName || codex.code}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                backgroundColor: 'common.white'
              }}
            />
          )}

          {showPlaceholder && !isLoadingPreview && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 1,
                color: 'text.secondary',
                p: 2,
                textAlign: 'center'
              }}
            >
              {iconForMimeType(codex.previewMimeType)}
              <Typography variant="caption" noWrap sx={{ width: '100%' }}>
                Anteprima non disponibile
              </Typography>
            </Box>
          )}

          {isLoadingPreview && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle1" noWrap>
            {codex.code}
          </Typography>
          {createdLabel !== 'N/A' && (
            <Typography variant="caption" color="text.secondary">
              {createdLabel}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

CodexThumbnailCard.propTypes = {
  codex: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    description: PropTypes.string,
    status: PropTypes.string,
    codexTypeDescription: PropTypes.string,
    codexTypeCode: PropTypes.string,
    previewUrl: PropTypes.string,
    previewMimeType: PropTypes.string,
    previewFileName: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  isLoadingPreview: PropTypes.bool,
};

export default CodexThumbnailCard;
